import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, AlertTriangle, Calendar } from "lucide-react";
import api from "../lib/api";
import { tagClass } from "../components/TagPicker";

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function Section({ title, icon: Icon, color, items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-6 fade-up" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${color}`} />
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-700">
          {title} · {items.length}
        </h3>
      </div>
      <div className="flex flex-col gap-2 fade-up-stagger">
        {items.map((l) => (
          <Link
            key={l.id}
            to={`/leads/${l.id}`}
            data-testid={`reminder-${l.id}`}
            className="p-4 border border-zinc-200 rounded-xl bg-white hover:-translate-y-0.5 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-display text-lg font-bold text-zinc-950 truncate">{l.name || "Unnamed"}</p>
                {l.company && <p className="text-sm text-zinc-600 truncate">{l.company}</p>}
                <p className="text-xs text-zinc-500 font-mono-num mt-1">{fmt(l.reminder_at)}</p>
              </div>
              {l.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-end max-w-[40%]">
                  {l.tags.slice(0, 2).map((t) => (
                    <span key={t} className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${tagClass(t)}`}>
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Reminders() {
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/reminders")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const total = data.overdue.length + data.today.length + data.upcoming.length;

  return (
    <div className="pb-32 fade-up" data-testid="reminders-page">
      <div className="px-6 pt-10 pb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Follow-ups</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none text-zinc-950 mt-1">
          Your <span className="text-blue-600">reminders</span>.
        </h1>
      </div>

      <div className="px-6">
        {loading ? (
          <div className="h-24 bg-zinc-100 rounded-xl animate-pulse-soft" />
        ) : total === 0 ? (
          <div className="text-center py-12" data-testid="reminders-empty">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
              <BellRing className="h-7 w-7 text-zinc-500" />
            </div>
            <h3 className="font-display text-xl font-bold text-zinc-950">No reminders set</h3>
            <p className="text-sm text-zinc-500 mt-1 mb-5">
              Open a lead and set a follow-up date to stay on top of your pipeline.
            </p>
            <Link
              to="/leads"
              className="inline-flex items-center gap-2 h-12 px-5 rounded-lg bg-zinc-950 text-white font-bold hover:bg-zinc-800 active:scale-95"
            >
              Browse leads
            </Link>
          </div>
        ) : (
          <>
            <Section
              title="Overdue"
              icon={AlertTriangle}
              color="text-red-600"
              items={data.overdue}
              testid="reminders-overdue"
            />
            <Section
              title="Due today"
              icon={BellRing}
              color="text-violet-700"
              items={data.today}
              testid="reminders-today"
            />
            <Section
              title="Upcoming"
              icon={Calendar}
              color="text-zinc-700"
              items={data.upcoming}
              testid="reminders-upcoming"
            />
          </>
        )}
      </div>
    </div>
  );
}
